/**
 * SalarieTabsExtras — Composants pour tabs Documents/Medical/Training/Interview
 * Ces 4 tabs consomment routes_hr_extras.py endpoints.
 */
import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import { Upload, Trash2, Plus, FileText, Stethoscope, GraduationCap, MessageSquare, Download, FileArchive } from "lucide-react";
import { toast } from "sonner";

const DOC_CATEGORIES = {
  id_card: "Carte d'identité", passport: "Passeport", rib: "RIB",
  cv: "CV", diploma: "Diplôme", driving_license: "Permis",
  proof_of_address: "Justificatif domicile", medical_certificate: "Certificat médical",
  other: "Autre",
};

/* ---------- Documents Tab ---------- */
export function DocumentsTab({ employeeId, canWrite }) {
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [category, setCategory] = useState("id_card");
  const [customLabel, setCustomLabel] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/hr/employees/${employeeId}/documents`); setRows(r.data.documents || []); }
    catch (e) { toast.error(apiError(e)); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Choisissez un fichier"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("category", category);
    if (customLabel) fd.append("custom_label", customLabel);
    try {
      await api.post(`/hr/employees/${employeeId}/documents`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté"); setDialog(false); setFile(null); setCustomLabel("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };
  const download = async (id, name) => {
    try {
      const r = await api.get(`/hr/documents/${id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try { await api.delete(`/hr/documents/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const dlZip = async () => {
    try {
      const r = await api.get(`/hr/exports/employees/${employeeId}/zip`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "dossier_salarie.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success("ZIP téléchargé");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Documents du salarié</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={dlZip} data-testid="btn-zip-emp">
              <FileArchive className="w-4 h-4 mr-1" /> Export ZIP
            </Button>
            {canWrite && (
              <Button size="sm" onClick={() => setDialog(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-upload-doc">
                <Upload className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">Aucun document. Versioning automatique par catégorie.</p>
            </div>
          ) : (
            <div className="divide-y">
              {rows.map((d, i) => (
                <div key={d.id} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`doc-row-${i}`}>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    {DOC_CATEGORIES[d.category] || d.category}
                  </Badge>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{d.custom_label || d.original_filename}</div>
                    <div className="text-xs text-slate-500 font-mono">
                      v{d.version} · {new Date(d.created_at).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => download(d.id, d.original_filename)}>
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  {canWrite && (
                    <Button size="sm" variant="ghost" onClick={() => remove(d.id)} className="text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ajouter un document</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Catégorie</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(DOC_CATEGORIES).map(([v, l]) =>
                  <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Libellé personnalisé (facultatif)</Label>
              <Input value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} />
            </div>
            <div><Label>Fichier</Label>
              <Input type="file" onChange={(e) => setFile(e.target.files?.[0])} data-testid="doc-file-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Annuler</Button>
            <Button onClick={upload} disabled={uploading} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="btn-doc-confirm">
              {uploading ? "..." : "Uploader"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ---------- Generic simple list tab (Medical/Training/Interview) ---------- */
function SimpleListTab({ employeeId, canWrite, endpoint, keyName, icon: Icon, title, fields }) {
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({});
  const load = useCallback(async () => {
    try { const r = await api.get(`/hr/employees/${employeeId}/${endpoint}`); setRows(r.data[keyName] || []); }
    catch (e) { toast.error(apiError(e)); }
  }, [employeeId, endpoint, keyName]);
  useEffect(() => { load(); setForm(fields.reduce((a, f) => ({ ...a, [f.key]: f.default || "" }), {})); }, [load, fields]);
  const submit = async () => {
    for (const f of fields.filter(x => x.required)) {
      if (!form[f.key]) { toast.error(`${f.label} requis`); return; }
    }
    try { await api.post(`/hr/employees/${employeeId}/${endpoint}`, form);
      toast.success("Créé"); setDialog(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    try { await api.delete(`/hr/${endpoint.slice(0, -1)}s/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const singular = endpoint.replace(/s$/, "");
  return (
    <>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Icon className="w-4 h-4" /> {title}
          </CardTitle>
          {canWrite && (
            <Button size="sm" onClick={() => setDialog(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`btn-add-${endpoint}`}>
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-6 text-center">
              <Icon className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">Aucun enregistrement.</p>
            </div>
          ) : (
            <div className="divide-y">
              {rows.map((r, i) => (
                <div key={r.id} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`${endpoint}-row-${i}`}>
                  <div className="flex-1 grid grid-cols-3 gap-2 text-sm">
                    {fields.slice(0, 3).map(f => (
                      <div key={f.key}>
                        <div className="text-xs text-slate-400">{f.label}</div>
                        <div className="font-medium">{f.options ? (f.options[r[f.key]] || r[f.key]) : (r[f.key] || "—")}</div>
                      </div>
                    ))}
                  </div>
                  {canWrite && (
                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau · {title}</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {fields.map(f => (
              <div key={f.key}>
                <Label>{f.label}{f.required && " *"}</Label>
                {f.options ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setForm(p => ({ ...p, [f.key]: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(f.options).map(([v, l]) =>
                      <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === "date" ? (
                  <DatePickerFR value={form[f.key] || ""}
                    onChange={(v) => setForm(p => ({ ...p, [f.key]: v }))} />
                ) : (
                  <Input type={f.type || "text"} value={form[f.key] || ""}
                    onChange={(e) => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Annuler</Button>
            <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid={`btn-save-${endpoint}`}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function MedicalTab({ employeeId, canWrite }) {
  return <SimpleListTab employeeId={employeeId} canWrite={canWrite}
    endpoint="medical-visits" keyName="visits" icon={Stethoscope} title="Visites médicales"
    fields={[
      { key: "visit_type", label: "Type", required: true, default: "periodic",
         options: { hire: "Embauche", periodic: "Périodique", return: "Reprise", occasional: "Occasionnelle" }},
      { key: "scheduled_date", label: "Date", type: "date", required: true },
      { key: "doctor_name", label: "Médecin" },
      { key: "location", label: "Lieu" },
      { key: "fitness", label: "Aptitude",
         options: { fit: "Apte", unfit_temporary: "Inapte temporaire", unfit_definitive: "Inapte définitif", pending: "En attente" }},
      { key: "next_visit_date", label: "Prochaine visite", type: "date" },
    ]} />;
}

export function TrainingTab({ employeeId, canWrite }) {
  return <SimpleListTab employeeId={employeeId} canWrite={canWrite}
    endpoint="trainings" keyName="trainings" icon={GraduationCap} title="Formations"
    fields={[
      { key: "title", label: "Titre", required: true },
      { key: "training_type", label: "Type", default: "external",
         options: { internal: "Interne", external: "Externe", e_learning: "E-learning" }},
      { key: "provider", label: "Organisme" },
      { key: "start_date", label: "Début", type: "date", required: true },
      { key: "end_date", label: "Fin", type: "date" },
      { key: "duration_hours", label: "Durée (h)", type: "number" },
      { key: "cost", label: "Coût (€)", type: "number" },
      { key: "status", label: "Statut", default: "planned",
         options: { planned: "Planifiée", in_progress: "En cours", completed: "Terminée", cancelled: "Annulée" }},
    ]} />;
}

export function InterviewTab({ employeeId, canWrite }) {
  return <SimpleListTab employeeId={employeeId} canWrite={canWrite}
    endpoint="interviews" keyName="interviews" icon={MessageSquare} title="Entretiens"
    fields={[
      { key: "interview_type", label: "Type", required: true, default: "annual",
         options: { annual: "Annuel", mid_year: "Mi-parcours", probation: "Fin d'essai",
                       professional: "Professionnel", offboarding: "Sortie" }},
      { key: "scheduled_date", label: "Date", type: "date", required: true },
      { key: "interviewer_name", label: "Interviewer" },
      { key: "status", label: "Statut", default: "planned",
         options: { planned: "Planifié", completed: "Réalisé", cancelled: "Annulé" }},
      { key: "notes", label: "Notes" },
    ]} />;
}
